import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import {
  CHART_BUILDER_QUESTION_EXAMPLES,
  EXAMPLE_STATUS_LABELS,
} from './chartBuilderQuestionExamples';

const CHART_TYPE_LABELS = {
  table: 'Tabla',
  bar: 'Barras',
  line: 'Líneas',
  pie: 'Torta',
  kpi: 'KPI',
};

/**
 * @param {object} props
 * @param {(example: object) => void} [props.onLoadExample]
 * @param {string|null} [props.activeExampleId]
 * @param {boolean} [props.loadDisabled]
 * @param {function} [props.getAuthHeaders] — para cargar/borrar ejemplos guardados
 * @param {number} [props.refreshKey] — incrementar para recargar lista guardada
 */
export default function ChartBuilderQuestionExamplesPanel({
  variant = 'full',
  defaultExpanded = false,
  intro,
  onLoadExample,
  activeExampleId = null,
  loadDisabled = false,
  getAuthHeaders,
  refreshKey = 0,
  allowDelete = true,
}) {
  const [sectionOpen, setSectionOpen] = useState(defaultExpanded);
  const [saved, setSaved] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadSavedMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/chart-builder/examples', { headers }),
  });

  const loadSaved = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers) {
      setSaved([]);
      return;
    }
    setLoadingSaved(true);
    try {
      const res = await loadSavedMutation.mutateAsync(headers);
      if (res.data.success) setSaved(res.data.examples || []);
    } catch {
      setSaved([]);
    } finally {
      setLoadingSaved(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadSaved();
  }, [loadSaved, refreshKey]);

  const deleteExampleMutation = useMutation({
    mutationFn: ({ id, headers }) =>
      axios.delete(`/api/chart-builder/examples/${encodeURIComponent(id)}`, { headers }),
  });

  const handleDelete = async (ex) => {
    const headers = getAuthHeaders?.();
    if (!headers || !ex?.id?.startsWith('saved-')) return;
    if (!window.confirm(`¿Eliminar el ejemplo «${ex.title}»?`)) return;
    setDeletingId(ex.id);
    try {
      const res = await deleteExampleMutation.mutateAsync({ id: ex.id, headers });
      if (res.data.success) setSaved(res.data.examples || []);
    } catch (err) {
      console.warn(err);
    } finally {
      setDeletingId(null);
    }
  };

  const allExamples = [
    ...saved.map((e) => ({ ...e, source: e.source || 'saved' })),
    ...CHART_BUILDER_QUESTION_EXAMPLES.map((e) => ({ ...e, source: 'builtin' })),
  ];

  const defaultIntro = onLoadExample
    ? 'Carga un ejemplo para ver la configuración en cada paso. Los «Guardados» salen de una query que publicaste o guardaste desde Vista previa; los de referencia vienen del producto.'
    : 'Ejemplos de referencia y los que hayas guardado desde el builder (Vista previa → Guardar como ejemplo).';

  return (
    <Accordion
      expanded={sectionOpen}
      onChange={(_, exp) => setSectionOpen(exp)}
      variant="outlined"
      sx={{ mt: 2, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <LightbulbOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Ejemplos de preguntas</Typography>
          <Chip size="small" label={allExamples.length} variant="outlined" />
          {loadingSaved && <CircularProgress size={14} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {intro || defaultIntro}
        </Alert>
        {allExamples.map((ex, idx) => {
          const statusMeta = EXAMPLE_STATUS_LABELS[ex.status] || EXAMPLE_STATUS_LABELS.planned;
          const isActive = activeExampleId === ex.id;
          const isSaved = ex.source === 'saved' || String(ex.id || '').startsWith('saved-');
          return (
            <Accordion
              key={ex.id}
              disableGutters
              variant="outlined"
              defaultExpanded={isActive}
              sx={{
                mb: idx < allExamples.length - 1 ? 1 : 0,
                borderColor: isActive ? 'primary.main' : undefined,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {ex.title}
                  </Typography>
                  <Chip size="small" label={statusMeta.label} color={statusMeta.color} variant="outlined" />
                  {isSaved && <Chip size="small" label="Desde builder" color="primary" variant="outlined" />}
                  {ex.suggestedChartType && (
                    <Chip
                      size="small"
                      label={CHART_TYPE_LABELS[ex.suggestedChartType] || ex.suggestedChartType}
                      variant="outlined"
                    />
                  )}
                  {isActive && <Chip size="small" color="primary" label="Cargado" />}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {variant === 'full' && (
                  <>
                    {ex.businessQuestion && (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Pregunta / nota
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {ex.businessQuestion}
                        </Typography>
                      </>
                    )}
                    {ex.businessTerms?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {ex.businessTerms.map((term) => (
                          <Chip key={term} size="small" label={term} sx={{ fontStyle: 'italic' }} />
                        ))}
                      </Box>
                    )}
                    {(ex.physicalModel || ex.sqlPreview) && (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          {isSaved ? 'SQL / modelo' : 'Traducción al modelo físico'}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            fontSize: '0.8125rem',
                            maxHeight: 160,
                            overflow: 'auto',
                          }}
                        >
                          {ex.sqlPreview || ex.physicalModel}
                        </Typography>
                      </>
                    )}
                    {ex.gaps?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {ex.gaps.map((gap) => (
                          <Chip key={gap} size="small" color="warning" variant="outlined" label={gap} />
                        ))}
                      </Box>
                    )}
                  </>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {typeof onLoadExample === 'function' && (
                    <Button
                      size="small"
                      variant={isActive ? 'outlined' : 'contained'}
                      startIcon={<PlayArrowIcon />}
                      disabled={loadDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        onLoadExample(ex);
                      }}
                    >
                      {isActive ? 'Recargar' : 'Cargar en el builder'}
                    </Button>
                  )}
                  {allowDelete && isSaved && getAuthHeaders && (
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deletingId === ex.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(ex);
                      }}
                      aria-label="Eliminar ejemplo"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {!onLoadExample && !isSaved && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Carga desde el <strong>Editor de gráficos</strong>.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Para guardar uno nuevo: en el builder, genera la vista previa y pulsa <strong>Guardar como ejemplo</strong>.
          Los de referencia del producto siguen en código; los guardados viven en la BD de la app.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export { CHART_BUILDER_QUESTION_EXAMPLES };
